import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { Paths } from '@config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { SELECT_AUTH_USER } from '@store/authentication/authentication.selection';

import { PaymentAtTableStoreSelector } from '@common/selectors/payment-at-table.selector';

import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { AnonymousAuthenticationService } from '@core/services/auth/anonymous-authentification.service';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { isPatMockMode, PAT_MOCK_CHECK_ID, resolvePatMockCheckId } from '@config/values/pat.values';
import {
  PAT_DEFAULT_MOCK_CHECK,
  PAT_DEFAULT_MOCK_CHECK_JSON,
} from '@config/fixtures/pat-mock-check.fixture';
import { DoodOpenCheck } from '@core/models/check.model';

@Component({
  selector: 'app-pat-page',
  templateUrl: './pat-page.component.html',
  standalone: false,
})
export class PatPageComponent extends DestroyerBase implements OnInit, OnDestroy {
  @Input() displayHeader = true;
  @Input() displayHeaderBack = true;
  @Input() headerText?: string;

  error!: string;

  /** When true, shows overlay to paste mock check JSON instead of calling API */
  isMockMode = signal(false);

  mockCheckJson = signal('');

  mockCheckParseError = signal<string | null>(null);

  /** When false, mock dialog is minimized to a small expand button */
  isMockDialogExpanded = signal(true);

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly anonymousAuthenticationService: AnonymousAuthenticationService,
    private paymentAtTableSelector: PaymentAtTableStoreSelector,
    private paymentAtTableDispatcher: PaymentAtTableStoreDispatcher,
    private readonly onSiteLocationService: OnSiteLocationsService,
    private readonly navigationHistoryService: NavigationHistoryService,
  ) {
    super();
    this.isMockMode.set(isPatMockMode(resolvePatMockCheckId(this.route.snapshot)));
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this._destroyerRef)).subscribe(params => {
      const mockMode = isPatMockMode(
        params['checkId'] ?? resolvePatMockCheckId(this.route.snapshot),
      );
      this.isMockMode.set(mockMode);
    });

    this.store
      .select(SELECT_AUTH_USER)
      .pipe(
        take(1),
        switchMap(user => (user?.id ? of(user) : this.signInUserAsAnonymous$().pipe(take(1)))),
        tap(user => {
          if (!user) {
            return;
          }
          if (this.isMockMode()) {
            this.ensureDefaultMockCheckLoaded();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.paymentAtTableDispatcher.resetCheck();
  }

  goBack(): void {
    if (this.isOnPaymentConfirmationPage()) {
      this.router.navigate(
        [this.routerHelper.translateRoute(`/${Paths.PayAtTable}/${Paths.PayAtTableOverview}`)],
        { queryParamsHandling: 'preserve' },
      );
      return;
    }

    this.navigationHistoryService.goBack();
  }

  private isOnPaymentConfirmationPage(): boolean {
    return this.router.url.includes(`/${Paths.PaymentConfirmation}/`);
  }

  signInUserAsAnonymous$(): Observable<DoodUserModel | undefined> {
    return this.anonymousAuthenticationService.createAnonymousSession$();
  }

  onMockCheckInputChange(): void {
    this.mockCheckParseError.set(null);
    const trimmed = this.mockCheckJson().trim();
    if (!trimmed) {
      return;
    }
    try {
      const parsed = JSON.parse(trimmed) as DoodOpenCheck;
      if (!parsed || typeof parsed.id !== 'string') {
        this.mockCheckParseError.set('Invalid format: check must have an id');
        return;
      }
      if (!parsed.onsite_location?.id) {
        this.mockCheckParseError.set('Invalid format: check must have onsite_location.id');
        return;
      }
      this.onSiteLocationService.injectMockCheck(parsed);
    } catch (e) {
      this.mockCheckParseError.set(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }

  private ensureDefaultMockCheckLoaded(): void {
    if (this.paymentAtTableSelector.check.id === PAT_MOCK_CHECK_ID) {
      if (!this.mockCheckJson().trim()) {
        this.mockCheckJson.set(PAT_DEFAULT_MOCK_CHECK_JSON);
      }
      return;
    }

    this.mockCheckJson.set(PAT_DEFAULT_MOCK_CHECK_JSON);
    this.onSiteLocationService.injectMockCheck(PAT_DEFAULT_MOCK_CHECK);
  }
}
